import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const HeaderContext = createContext({
  collapsed: false,
  setCollapsed: () => {},
  loading: false,
  setLoading: () => {},
  navFadePhase: "idle",
  navFadeOutMs: 1500,
  startNavFade: () => {},
});

// Timings for the hamburger → landing fade. Single overlay covers everything
// (header included), fades in, we navigate, then it fades back out.
const NAV_FADE_IN_MS = 500;       // overlay fade-in (everything to black)
const NAV_FADE_OUT_MS = 1500;     // overlay fade-out (reveal landing slowly)
// Hold the overlay fully opaque briefly after navigate() so PageTransition's
// own 150ms cross-fade finishes swapping under the black, otherwise the
// previous page bleeds through at the start of the fade-out.
const NAV_FADE_HOLD_AFTER_NAV_MS = 220;

export function HeaderProvider({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  // navFadePhase: "idle" | "active" | "overlay-out"
  const [navFadePhase, setNavFadePhase] = useState("idle");
  // Fade-out duration for the current run. Callers can shorten it: the
  // product page's back arrow wants a quicker return than the landing reveal.
  const [navFadeOutMs, setNavFadeOutMs] = useState(NAV_FADE_OUT_MS);
  const timersRef = useRef([]);
  const onNavigateRef = useRef(null);

  const clearTimers = () => {
    for (const id of timersRef.current) clearTimeout(id);
    timersRef.current = [];
  };

  const startNavFade = useCallback((onNavigate, { outMs = NAV_FADE_OUT_MS } = {}) => {
    clearTimers();
    onNavigateRef.current = onNavigate;
    setNavFadeOutMs(outMs);
    setNavFadePhase("active");
    const push = (fn, ms) => {
      const id = setTimeout(fn, ms);
      timersRef.current.push(id);
    };
    // At fade-in end: navigate, but keep overlay fully black while
    // PageTransition swaps the underlying page.
    push(() => onNavigateRef.current?.(), NAV_FADE_IN_MS);
    push(() => setNavFadePhase("overlay-out"), NAV_FADE_IN_MS + NAV_FADE_HOLD_AFTER_NAV_MS);
    push(
      () => setNavFadePhase("idle"),
      NAV_FADE_IN_MS + NAV_FADE_HOLD_AFTER_NAV_MS + outMs + 50,
    );
  }, []);

  useEffect(() => () => clearTimers(), []);

  return (
    <HeaderContext.Provider value={{ collapsed, setCollapsed, loading, setLoading, navFadePhase, navFadeOutMs, startNavFade }}>
      {children}
    </HeaderContext.Provider>
  );
}

export function useHeader() {
  return useContext(HeaderContext);
}
